import * as THREE from 'three';

/**
 * Snow (and ice) materials that blend the dynamic paint texture into a
 * MeshStandardMaterial via onBeforeCompile — so lighting, shadows, and fog
 * all keep working across every location.
 *
 * Paint reads as *in* the snow: it is slightly desaturated, veiled toward
 * white ("frost"), and modulated by procedural grain so the snow's surface
 * texture shows through faintly.
 */

/** A fresh paint-texture uniform, initialized to a 1×1 transparent texture.
 * Locations create one per paintable surface; the PaintSurface adopts it. */
export function createPaintUniform(): { value: THREE.Texture } {
  const tex = new THREE.DataTexture(new Uint8Array([0, 0, 0, 0]), 1, 1);
  tex.needsUpdate = true;
  return { value: tex };
}

export interface SnowMaterialOptions {
  /** Live paint texture uniform from a PaintSurface (premultiplied alpha). */
  paintUniform: { value: THREE.Texture };
  /** Base snow tint. */
  color?: THREE.ColorRepresentation;
  roughness?: number;
  /** 0..1 — how much the paint is frosted/desaturated into the surface. */
  frost?: number;
  /** When true, geometry must provide a float `aSnow` attribute (0 = bare
   * ground such as wet sand, 1 = full snow). Paint only sticks to snow. */
  useSnowMask?: boolean;
  /** Surface color where aSnow is 0 (e.g. sand, asphalt). */
  bareColor?: THREE.ColorRepresentation;
  /** Grain frequency in UV space — scale with the mesh's world size. */
  grainScale?: number;
}

export function createSnowMaterial(options: SnowMaterialOptions): THREE.MeshStandardMaterial {
  const {
    paintUniform,
    color = 0xf4f7fc,
    roughness = 0.92,
    frost = 1,
    useSnowMask = false,
    bareColor = 0xc2b280,
    grainScale = 900,
  } = options;

  const material = new THREE.MeshStandardMaterial({
    color,
    roughness,
    metalness: 0,
  });

  material.defines = { USE_UV: '' };
  if (useSnowMask) material.defines.USE_SNOWMASK = '';

  material.onBeforeCompile = (shader) => {
    shader.uniforms.uPaintMap = paintUniform;
    shader.uniforms.uFrost = { value: frost };
    shader.uniforms.uGrainScale = { value: grainScale };
    shader.uniforms.uBareColor = { value: new THREE.Color(bareColor) };

    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        /* glsl */ `#include <common>
        #ifdef USE_SNOWMASK
          attribute float aSnow;
          varying float vSnowMask;
        #endif`,
      )
      .replace(
        '#include <begin_vertex>',
        /* glsl */ `#include <begin_vertex>
        #ifdef USE_SNOWMASK
          vSnowMask = aSnow;
        #endif`,
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        /* glsl */ `#include <common>
        uniform sampler2D uPaintMap;
        uniform float uFrost;
        uniform float uGrainScale;
        uniform vec3 uBareColor;
        #ifdef USE_SNOWMASK
          varying float vSnowMask;
        #endif
        float sgHash(vec2 p) {
          p = fract(p * vec2(123.34, 456.21));
          p += dot(p, p + 45.32);
          return fract(p.x * p.y);
        }
        float sgNoise(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          vec2 u2 = f * f * (3.0 - 2.0 * f);
          return mix(
            mix(sgHash(i), sgHash(i + vec2(1.0, 0.0)), u2.x),
            mix(sgHash(i + vec2(0.0, 1.0)), sgHash(i + vec2(1.0, 1.0)), u2.x),
            u2.y
          );
        }`,
      )
      .replace(
        '#include <color_fragment>',
        /* glsl */ `#include <color_fragment>
        {
          float grain = sgNoise(vUv * uGrainScale) * 0.65 + sgNoise(vUv * uGrainScale * 3.7) * 0.35;
          float snowMask = 1.0;
          #ifdef USE_SNOWMASK
            snowMask = clamp(vSnowMask, 0.0, 1.0);
            diffuseColor.rgb = mix(uBareColor, diffuseColor.rgb, smoothstep(0.05, 0.55, snowMask));
          #endif
          // Subtle sparkle/grain on the snow itself.
          diffuseColor.rgb *= 0.955 + 0.075 * grain;

          vec4 paint = texture2D(uPaintMap, vUv);
          float cover = paint.a * snowMask;
          if (cover > 0.001) {
            vec3 pc = paint.rgb / max(paint.a, 1e-4); // un-premultiply
            float lum = dot(pc, vec3(0.299, 0.587, 0.114));
            pc = mix(pc, vec3(lum), uFrost * 0.2);     // desaturate into the snow
            pc = mix(pc, vec3(1.0), uFrost * 0.12);    // frost veil
            // Grain lets the snow surface show through the paint faintly.
            cover *= 0.86 + 0.14 * grain;
            diffuseColor.rgb = mix(diffuseColor.rgb, pc, min(cover, 1.0) * 0.95);
          }
        }`,
      );
  };

  // Distinct programs for masked vs unmasked variants.
  material.customProgramCacheKey = () => `snow-graffiti-snow|${useSnowMask ? 'mask' : 'plain'}`;
  return material;
}

/** Glossier variant for frozen pond / lake ice — paint sits crisper on ice. */
export function createIceMaterial(
  options: Omit<SnowMaterialOptions, 'frost' | 'roughness'> & { color?: THREE.ColorRepresentation },
): THREE.MeshStandardMaterial {
  return createSnowMaterial({
    roughness: 0.28,
    frost: 0.35,
    color: 0xdfeaf4,
    ...options,
  });
}
